// Help panel — renders the full player command catalog from the shared
// commands registry (HOO-52). Opened by `?` / `/help` / HUD button.
// Grammar-gated: only meaningful during phase=playing.

import {
  COMMANDS, CATEGORY_ORDER, CATEGORY_LABELS, commandsByCategory, type CommandSpec,
} from "../../shared/commands.js";

export function openHelpPanel(host?: HTMLElement): HTMLElement {
  const container = host ?? document.body;
  // Remove any existing help overlay
  const existing = container.querySelector<HTMLElement>(".help-overlay");
  if (existing) { existing.remove(); return existing; }

  const overlay = document.createElement("div");
  overlay.className = "help-overlay";
  overlay.innerHTML = renderHtml();
  container.appendChild(overlay);

  const input = overlay.querySelector<HTMLInputElement>("#help-search")!;
  const listHost = overlay.querySelector<HTMLElement>("#help-list")!;
  const closeBtn = overlay.querySelector<HTMLButtonElement>("#help-close")!;

  function filter(q: string): void {
    const query = q.toLowerCase().trim();
    const cats = commandsByCategory();
    listHost.innerHTML = CATEGORY_ORDER.map((cat) => {
      const entries = (cats[cat] ?? []).filter((c) => matches(c, query));
      if (entries.length === 0) return "";
      return `
        <section class="help-category">
          <h3>${escape(CATEGORY_LABELS[cat])}</h3>
          <table class="help-table">
            <tbody>
              ${entries.map(renderRow).join("")}
            </tbody>
          </table>
        </section>
      `;
    }).filter(Boolean).join("");
  }

  function renderRow(c: CommandSpec): string {
    const aliases = c.aliases.join(", ");
    return `
      <tr>
        <td class="help-syntax"><code>${escape(c.syntax)}</code></td>
        <td class="help-aliases"><code>${escape(aliases)}</code></td>
        <td class="help-summary">${escape(c.summary)}<div class="help-example"><span class="dim">e.g.</span> <code>${escape(c.example)}</code></div></td>
      </tr>
    `;
  }

  function cleanup(): void {
    document.removeEventListener("keydown", onEsc);
    overlay.remove();
  }
  function onEsc(ev: KeyboardEvent): void { if (ev.key === "Escape") cleanup(); }
  (overlay as unknown as { __cleanup: () => void }).__cleanup = cleanup;
  input.addEventListener("input", (e) => filter((e.target as HTMLInputElement).value));
  closeBtn.addEventListener("click", cleanup);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) cleanup(); });
  document.addEventListener("keydown", onEsc);

  filter("");
  input.focus();
  return overlay;
}

function renderHtml(): string {
  return `
    <div class="help-modal" role="dialog" aria-label="Help">
      <header class="help-header">
        <h2>Commands — ${COMMANDS.length} total</h2>
        <input id="help-search" type="search" placeholder="Filter by name, alias, or summary" autocomplete="off" />
        <button id="help-close" class="help-close" aria-label="Close">×</button>
      </header>
      <div id="help-list" class="help-list"></div>
      <footer class="help-footer">
        <span class="dim">Press <kbd>Esc</kbd> to close — click backdrop to dismiss.</span>
      </footer>
    </div>
  `;
}

function matches(c: CommandSpec, q: string): boolean {
  if (!q) return true;
  if (c.id.includes(q) || c.syntax.toLowerCase().includes(q)) return true;
  if (c.summary.toLowerCase().includes(q)) return true;
  if (c.example.toLowerCase().includes(q)) return true;
  for (const a of c.aliases) if (a.toLowerCase().includes(q)) return true;
  return false;
}

function escape(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]!));
}
